/**
 * Property settings tabs: the allowlist `?tab=` deep links are validated
 * against, and the canonical href builder for linking into a tab (and a card
 * on it) from emails, alerts and other pages.
 *
 * Pure and browser-safe.
 */

export const PROPERTY_SETTINGS_TABS = [
	"general",
	"api-key",
	"reports",
	"notifications",
	"client-portal",
	"billing",
	"experimental",
	"danger",
] as const;

export type PropertySettingsTab = (typeof PROPERTY_SETTINGS_TABS)[number];

export const DEFAULT_PROPERTY_SETTINGS_TAB: PropertySettingsTab = "general";

export interface ResolveSettingsTabOptions {
	/** The plan-gated client-portal tab is visible for this property. */
	showClientPortalTab: boolean;
}

export function isPropertySettingsTab(
	value: unknown,
): value is PropertySettingsTab {
	return (
		typeof value === "string" &&
		(PROPERTY_SETTINGS_TABS as readonly string[]).includes(value)
	);
}

/**
 * Resolve a raw `?tab=` value to a tab that exists for this property. Unknown
 * values, and a gated tab the property can't see, fall back to `general`.
 */
export function resolveSettingsTab(
	raw: string | string[] | null | undefined,
	options: ResolveSettingsTabOptions,
): PropertySettingsTab {
	const value = (Array.isArray(raw) ? raw[0] : raw)?.trim().toLowerCase();
	if (!isPropertySettingsTab(value)) return DEFAULT_PROPERTY_SETTINGS_TAB;
	if (value === "client-portal" && !options.showClientPortalTab) {
		return DEFAULT_PROPERTY_SETTINGS_TAB;
	}
	return value;
}

export interface PropertySettingsHrefInput {
	organizationSlug: string;
	propertyId: string;
	tab?: PropertySettingsTab;
	/** Card anchor id on the tab, without the `#`. */
	anchor?: string;
	/** Prefix with an absolute origin (e.g. NEXT_PUBLIC_SITE_URL) for emails. */
	baseUrl?: string;
}

/**
 * `/app/{org}/properties/{id}/settings?tab={tab}#{anchor}`. The default tab is
 * omitted from the query so plain settings links stay clean.
 */
export function propertySettingsHref({
	organizationSlug,
	propertyId,
	tab,
	anchor,
	baseUrl,
}: PropertySettingsHrefInput): string {
	const base = baseUrl ? baseUrl.replace(/\/+$/, "") : "";
	let href = `${base}/app/${encodeURIComponent(organizationSlug)}/properties/${encodeURIComponent(propertyId)}/settings`;
	if (tab && tab !== DEFAULT_PROPERTY_SETTINGS_TAB) {
		href += `?tab=${tab}`;
	}
	if (anchor) {
		href += `#${anchor.replace(/^#/, "")}`;
	}
	return href;
}
